import { useMemo } from 'react';
import { Country } from '@prisma/client';
import { getStateOptions, getSubdivisionName } from '@commenda/utils';
import { useContentLibraryIndirectTaxAddressStates } from './useContentLibraryIndirectTaxAddressStates';

export interface ContentBasedStateOption {
  value: string;
  label: string;
}

/**
 * State/subdivision options for an address picker.
 *
 * When a `corporationId` is provided the options come from backend content
 * (`valid_state_codes`) humanized via CLDR — so the picker only offers codes the
 * sales-tax service will accept and shows "Auvergne-Rhône-Alpes" instead of "ARA".
 * Without a `corporationId` it falls back to the `country-state-city` library
 * (the legacy behavior for non-location forms).
 *
 * Centralizes the fetch + humanization that the location modals and the
 * indirect-tax / registration address inputs would otherwise each duplicate. The
 * underlying request is keyed on (corporationId, country), so SWR dedupes it
 * across every component rendering the same picker.
 */
export function useContentBasedStateOptions(
  corporationId: number | null | undefined,
  country: string | null | undefined
): ContentBasedStateOption[] {
  const { data } = useContentLibraryIndirectTaxAddressStates(
    typeof corporationId === 'number' && country
      ? { corporationId, country }
      : null
  );

  return useMemo(() => {
    if (!country) return [];
    if (typeof corporationId === 'number') {
      return (data?.options ?? []).map((option) => ({
        value: option.value,
        label: getSubdivisionName(country, option.value) || option.value,
      }));
    }
    // `country` is validated against the Country enum upstream; the cast keeps
    // the country-state-city fallback typed without re-validating here.
    return getStateOptions(country as Country).map(({ code, name }) => ({
      value: code,
      label: name,
    }));
  }, [corporationId, data, country]);
}
